/**
 * Preferencias STT (modelos live/final, Web Speech, timestamps UI).
 */
import {
  pickWhisperModelTier,
  whisperModelIdForTier,
  probeDeviceCapability,
  getWhisperModelOverride,
} from './deviceCapability'
import { isLiveSpeechSupported } from './liveSpeech'
import { getSttRuntimeSnapshot } from './sttRuntime'
import { DEVICE_TYPES } from './types'

/** @typedef {'tiny'|'base'|'small'} WhisperModelTier */
/** @typedef {'auto'|WhisperModelTier} WhisperModelChoice */

const KEY_FINAL = 'udv.voiceNotes.whisperModel'
const KEY_LIVE = 'udv.voiceNotes.whisperLiveModel'
const KEY_LIVE_STT = 'udv.voiceNotes.liveStt'
const KEY_SHOW_TS = 'udv.voiceNotes.showTimestamps'
const KEY_TRANSCRIBE_DESKTOP = 'udv.voiceNotes.transcribeDesktop'
const KEY_TRANSCRIBE_MOBILE = 'udv.voiceNotes.transcribeMobile'
const KEY_LOCAL_WHISPER_LEGACY = 'udv.voiceNotes.localWhisper'

export const WHISPER_MODEL_META = {
  tiny: {
    tier: 'tiny',
    label: 'Tiny',
    sizeHint: '~40 MB',
    liveHint: 'Mejor para live (rápido)',
    finalHint: 'OK calidad básica',
  },
  base: {
    tier: 'base',
    label: 'Base',
    sizeHint: '~140 MB',
    liveHint: 'Live solo en PCs potentes',
    finalHint: 'Buen equilibrio calidad/velocidad',
  },
  small: {
    tier: 'small',
    label: 'Small',
    sizeHint: '~240 MB+',
    liveHint: 'No recomendado en live',
    finalHint: 'Mejor calidad al finalizar / re-transcribir',
  },
}

/**
 * @param {string} key
 * @param {boolean} defaultValue
 * @returns {boolean}
 */
function readBoolPref(key, defaultValue) {
  try {
    const v = localStorage.getItem(key)
    if (v == null || v === '') return defaultValue
    return v === '1' || v === 'true' || v === 'on'
  } catch (e) {
    return defaultValue
  }
}

/**
 * @param {string} key
 * @param {boolean} on
 */
function writeBoolPref(key, on) {
  try {
    localStorage.setItem(key, on ? 'true' : 'false')
  } catch (e) {
    /* ignore */
  }
}

/**
 * Transcripción Whisper local en desktop (default on).
 * Compatible con flag legacy `udv.voiceNotes.localWhisper`.
 * @returns {boolean}
 */
export function getTranscribeDesktopPref() {
  try {
    const v = localStorage.getItem(KEY_TRANSCRIBE_DESKTOP)
    if (v != null && v !== '') {
      return readBoolPref(KEY_TRANSCRIBE_DESKTOP, true)
    }
    const legacy = localStorage.getItem(KEY_LOCAL_WHISPER_LEGACY)
    if (legacy != null && legacy !== '') {
      return legacy !== '0' && legacy !== 'false' && legacy !== 'off'
    }
    return true
  } catch (e) {
    return true
  }
}

/**
 * @param {boolean} on
 */
export function setTranscribeDesktopPref(on) {
  writeBoolPref(KEY_TRANSCRIBE_DESKTOP, !!on)
}

/**
 * Transcripción en móvil (live / futuro Gateway; default on).
 * @returns {boolean}
 */
export function getTranscribeMobilePref() {
  return readBoolPref(KEY_TRANSCRIBE_MOBILE, true)
}

/**
 * @param {boolean} on
 */
export function setTranscribeMobilePref(on) {
  writeBoolPref(KEY_TRANSCRIBE_MOBILE, !!on)
}

/**
 * @param {ReturnType<typeof probeDeviceCapability>} [cap]
 * @returns {boolean}
 */
export function isMobileVoiceDevice(cap) {
  const p = cap || probeDeviceCapability()
  return (
    p.device_type === DEVICE_TYPES.MOBILE_PWA ||
    p.whisperSkipReason === 'mobile_ua'
  )
}

/**
 * ¿Transcribir en el dispositivo actual? (independiente de grabación).
 * @param {ReturnType<typeof probeDeviceCapability>} [cap]
 * @returns {boolean}
 */
export function isTranscriptionEnabledForDevice(cap) {
  const p = cap || probeDeviceCapability()
  if (isMobileVoiceDevice(p)) return getTranscribeMobilePref()
  return getTranscribeDesktopPref()
}

/**
 * ¿Puede ejecutarse pipeline de transcripción ahora?
 * Desktop: pref + hardware Whisper. Móvil: solo pref (live / gateway).
 * @param {ReturnType<typeof probeDeviceCapability>} [cap]
 * @returns {boolean}
 */
export function canTranscribeVoiceNotes(cap) {
  const p = cap || probeDeviceCapability()
  if (!isTranscriptionEnabledForDevice(p)) return false
  if (isMobileVoiceDevice(p)) return true
  return !!p.canLocalWhisper
}

/**
 * Chrome / Edge / Chromium con Web Speech usable.
 * @returns {boolean}
 */
export function isChromeLikeSpeechBrowser() {
  if (!isLiveSpeechSupported()) return false
  try {
    const ua = String(navigator.userAgent || '')
    // Brave se identifica como Chrome pero suele fallar network — no ofrecemos como default
    if (/Brave/i.test(ua) || (navigator.brave && typeof navigator.brave.isBrave === 'function')) {
      return false
    }
    return /Chrome|Edg|CriOS/i.test(ua) && !/Firefox/i.test(ua)
  } catch (e) {
    return isLiveSpeechSupported()
  }
}

/**
 * @param {string|null|undefined} v
 * @returns {WhisperModelChoice|null}
 */
function parseChoice(v) {
  const s = String(v || '')
    .trim()
    .toLowerCase()
  if (!s || s === 'auto') return 'auto'
  if (s === 'tiny' || s === 'base' || s === 'small') return s
  if (s.includes('small')) return 'small'
  if (s.includes('base')) return 'base'
  if (s.includes('tiny')) return 'tiny'
  return null
}

/**
 * @returns {WhisperModelChoice} auto|tiny|base|small
 */
export function getFinalModelChoice() {
  try {
    const v = parseChoice(localStorage.getItem(KEY_FINAL))
    return v || 'auto'
  } catch (e) {
    return 'auto'
  }
}

/**
 * @param {WhisperModelChoice} choice
 */
export function setFinalModelChoice(choice) {
  try {
    const c = parseChoice(choice) || 'auto'
    if (c === 'auto') localStorage.removeItem(KEY_FINAL)
    else localStorage.setItem(KEY_FINAL, c)
  } catch (e) {
    /* ignore */
  }
}

/**
 * Override explícito del modelo live (auto = derivar del final/heurística).
 * @returns {WhisperModelChoice}
 */
export function getLiveModelChoice() {
  try {
    const v = parseChoice(localStorage.getItem(KEY_LIVE))
    return v || 'auto'
  } catch (e) {
    return 'auto'
  }
}

/**
 * @param {WhisperModelChoice} choice
 */
export function setLiveModelChoice(choice) {
  try {
    const c = parseChoice(choice) || 'auto'
    if (c === 'auto') localStorage.removeItem(KEY_LIVE)
    else localStorage.setItem(KEY_LIVE, c)
  } catch (e) {
    /* ignore */
  }
}

/**
 * @returns {boolean}
 */
export function getWebSpeechPref() {
  try {
    const v = localStorage.getItem(KEY_LIVE_STT)
    return v === '1' || v === 'true' || v === 'on'
  } catch (e) {
    return false
  }
}

/**
 * @param {boolean} on
 */
export function setWebSpeechPref(on) {
  try {
    if (on) localStorage.setItem(KEY_LIVE_STT, 'true')
    else localStorage.setItem(KEY_LIVE_STT, 'false')
  } catch (e) {
    /* ignore */
  }
}

/**
 * @returns {boolean}
 */
export function getShowTimestampsPref() {
  try {
    const v = localStorage.getItem(KEY_SHOW_TS)
    return v === '1' || v === 'true' || v === 'on'
  } catch (e) {
    return false
  }
}

/**
 * @param {boolean} on
 */
export function setShowTimestampsPref(on) {
  try {
    localStorage.setItem(KEY_SHOW_TS, on ? 'true' : 'false')
  } catch (e) {
    /* ignore */
  }
}

/**
 * Sugerencias según hardware.
 * @param {ReturnType<typeof probeDeviceCapability>} [cap]
 */
export function suggestSttModels(cap) {
  const p = cap || probeDeviceCapability()
  const finalSuggested = p.canLocalWhisper
    ? pickWhisperModelTier({
        hardwareConcurrency: p.hardwareConcurrency,
        deviceMemory: p.deviceMemory,
        hasWebGpu: p.hasWebGpu,
        canLocalWhisper: true,
      })
    : 'tiny'

  /** Live: un escalón más liviano que final */
  let liveSuggested = 'tiny'
  if (finalSuggested === 'small') liveSuggested = 'base'
  else if (finalSuggested === 'base' && p.hasWebGpu && p.hardwareConcurrency >= 6) {
    liveSuggested = 'tiny'
  } else {
    liveSuggested = 'tiny'
  }

  const backend = p.hasWebGpu ? 'WebGPU (si ORT lo usa) / WASM' : 'WASM / CPU'

  return {
    liveSuggested,
    finalSuggested,
    liveReason:
      liveSuggested === 'tiny'
        ? 'Tiny: menor latencia en dictado en vivo'
        : 'Base: mejor live en máquina potente (más RAM/CPU)',
    finalReason:
      finalSuggested === 'small'
        ? 'Small: mejor calidad al cortar / re-transcribir'
        : finalSuggested === 'base'
          ? 'Base: buen equilibrio al finalizar'
          : 'Tiny: suficiente en hardware justo',
    backend,
    canLocalWhisper: !!p.canLocalWhisper,
    canLiveLocalStt: !!p.canLiveLocalStt,
    hasWebGpu: !!p.hasWebGpu,
    hardwareConcurrency: p.hardwareConcurrency,
    deviceMemory: p.deviceMemory,
    finalActive: p.whisperModelTier,
    liveActive: p.liveModelTier,
    chromeSpeechUi: isChromeLikeSpeechBrowser(),
  }
}

/**
 * Snapshot para UI de ajustes.
 */
export function getSttPrefsSnapshot() {
  const cap = probeDeviceCapability()
  const suggestions = suggestSttModels(cap)
  return {
    ...suggestions,
    finalChoice: getFinalModelChoice(),
    liveChoice: getLiveModelChoice(),
    webSpeech: getWebSpeechPref(),
    showTimestamps: getShowTimestampsPref(),
    transcribeDesktop: getTranscribeDesktopPref(),
    transcribeMobile: getTranscribeMobilePref(),
    transcriptionOnDevice: isTranscriptionEnabledForDevice(cap),
    isMobileVoiceDevice: isMobileVoiceDevice(cap),
    finalOverride: getWhisperModelOverride(),
    modelMeta: WHISPER_MODEL_META,
    sttRuntime: getSttRuntimeSnapshot(),
  }
}

/**
 * Resuelve modelo live efectivo (choice + heurística).
 * @returns {{ tier: WhisperModelTier, modelId: string, suggested: WhisperModelTier }}
 */
export function resolveLiveModelFromPrefs() {
  const suggestions = suggestSttModels()
  const choice = getLiveModelChoice()
  const tier =
    choice === 'auto' || !choice
      ? suggestions.liveSuggested
      : /** @type {WhisperModelTier} */ (choice)
  return {
    tier,
    modelId: whisperModelIdForTier(tier),
    suggested: suggestions.liveSuggested,
  }
}

export {
  KEY_FINAL,
  KEY_LIVE,
  KEY_LIVE_STT,
  KEY_SHOW_TS,
  KEY_TRANSCRIBE_DESKTOP,
  KEY_TRANSCRIBE_MOBILE,
}

export default {
  getSttPrefsSnapshot,
  suggestSttModels,
  setFinalModelChoice,
  setLiveModelChoice,
  setWebSpeechPref,
  setShowTimestampsPref,
  getTranscribeDesktopPref,
  setTranscribeDesktopPref,
  getTranscribeMobilePref,
  setTranscribeMobilePref,
  isMobileVoiceDevice,
  isTranscriptionEnabledForDevice,
  canTranscribeVoiceNotes,
}
